import type { Position } from "geojson";
import { getJobBoundaryGeometry, type Job } from "@domain/CRM/job";
import { polygonBboxCenter } from "@domain/Geo/geometry";
import { shiftIdByJobId } from "./jobShiftAttribution";
import { IDLE_VEHICLE_COLOR } from "./vehicleShiftColors";

/**
 * The few shift colors of the "spaced" scheme — an alternative to hashing each robot's name to a
 * hue, which yields many near-identical colors. Greens and blues plus purple and pink; never red,
 * orange or yellow, which stay free for what must stand out (selection orange, lasso and backlog
 * yellow, error red). Far enough apart in hue and lightness to be told apart at a glance.
 * Ordered most-distinct first: the first shifts placed take the front of the list.
 */
export const SPACED_SHIFT_PALETTE: readonly string[] = [
	"#2ca02c", // green
	"#0ea5e9", // azure
	"#9333ea", // purple
	"#5eead4", // aqua
	"#ec4899", // pink
	"#1e3a8a", // navy
	"#0f5e55", // dark teal
];

/** A shift to be colored, with where its jobs are. */
export interface ShiftToColor {
	id: string;
	/** `[lng, lat]` of each of the shift's jobs. Empty when the shift has no job on the map. */
	points: readonly Position[];
	/** Whether the shift has any job at all. Only matters when `points` is empty: a shift with
	 *  jobs that merely aren't on the map (no boundary yet) still gets a color. Defaults to
	 *  "has a job on the map". */
	hasJobs?: boolean;
}

interface PlacedShift {
	points: readonly Position[];
	/** `[west, south, east, north]` of `points`, for skipping far-apart pairs cheaply. */
	bbox: [number, number, number, number];
}

/** Squared distance between two `[lng, lat]` points, in degrees of latitude — longitude is
 *  shrunk by the latitude's cosine. Only ever compared with itself, so it stays squared. */
function gapSquared(a: Position, b: Position): number {
	const dLat = a[1] - b[1];
	const dLng = (a[0] - b[0]) * Math.cos(((a[1] + b[1]) / 2) * (Math.PI / 180));
	return dLat * dLat + dLng * dLng;
}

function bboxOf(points: readonly Position[]): PlacedShift["bbox"] {
	let [west, south, east, north] = [Infinity, Infinity, -Infinity, -Infinity];
	for (const [lng, lat] of points) {
		west = Math.min(west, lng);
		east = Math.max(east, lng);
		south = Math.min(south, lat);
		north = Math.max(north, lat);
	}
	return [west, south, east, north];
}

/** A lower bound on {@link shiftGapSquared}: how far apart the two bounding boxes are. Longitude
 *  is shrunk by the cosine at the boxes' highest latitude — the smallest factor any pair of their
 *  points could get — so this never over-states the gap, and a pair whose bound cannot beat a gap
 *  already found can be skipped without scanning its points. */
function bboxGapSquared(a: PlacedShift["bbox"], b: PlacedShift["bbox"]): number {
	const highestLat = Math.max(Math.abs(a[1]), Math.abs(a[3]), Math.abs(b[1]), Math.abs(b[3]));
	const dLng = Math.max(0, a[0] - b[2], b[0] - a[2]) * Math.cos(highestLat * (Math.PI / 180));
	const dLat = Math.max(0, a[1] - b[3], b[1] - a[3]);
	return dLat * dLat + dLng * dLng;
}

/** How close two shifts come: the smallest gap between any job of one and any job of the other. */
function shiftGapSquared(a: PlacedShift, b: PlacedShift, stopBelow: number): number {
	let nearest = Infinity;
	for (const p of a.points) {
		for (const q of b.points) {
			const gap = gapSquared(p, q);
			if (gap < nearest) {
				nearest = gap;
				// Already closer than a rival color's nearest shift: this color has lost.
				if (nearest <= stopBelow) return nearest;
			}
		}
	}
	return nearest;
}

/**
 * Color each shift from a small palette so that shifts working near each other differ.
 *
 * With only a few colors, shifts must share them — the aim is that two shifts wearing the same
 * color are as far apart as possible. Shifts are placed in the order given, each taking the color
 * whose nearest already-placed wearer is FARTHEST away (an unused color counts as infinitely far,
 * so the palette is used up first).
 *
 * A shift with no job at all is drawn idle gray. One whose jobs just aren't on the map (none has
 * a boundary) has no neighbors to keep away from, so it takes the least-used color so far.
 *
 * Placement order matters: an earlier shift never changes color because a later one was added.
 */
export function assignSpacedShiftColors(
	shifts: readonly ShiftToColor[],
	palette: readonly string[] = SPACED_SHIFT_PALETTE,
): Map<string, string> {
	const colorByShiftId = new Map<string, string>();
	const placedByColor: PlacedShift[][] = palette.map(() => []);
	const wearers = palette.map(() => 0);
	for (const shift of shifts) {
		if (shift.points.length === 0) {
			if (!shift.hasJobs) {
				colorByShiftId.set(shift.id, IDLE_VEHICLE_COLOR);
				continue;
			}
			const leastUsed = wearers.indexOf(Math.min(...wearers));
			wearers[leastUsed]++;
			colorByShiftId.set(shift.id, palette[leastUsed]);
			continue;
		}
		const placing: PlacedShift = { points: shift.points, bbox: bboxOf(shift.points) };
		let best = 0;
		let bestGap = -1;
		for (let color = 0; color < palette.length; color++) {
			// The gap to this color's nearest wearer. It only matters if it beats `bestGap`, so
			// both the bbox bound and the point scan give up as soon as it can't.
			let gap = Infinity;
			for (const placed of placedByColor[color]) {
				if (bboxGapSquared(placing.bbox, placed.bbox) >= gap) continue;
				gap = Math.min(gap, shiftGapSquared(placing, placed, bestGap));
				if (gap <= bestGap) break;
			}
			// Strictly greater: on a tie the earlier palette color wins.
			if (gap > bestGap) {
				best = color;
				bestGap = gap;
			}
		}
		placedByColor[best].push(placing);
		wearers[best]++;
		colorByShiftId.set(shift.id, palette[best]);
	}
	return colorByShiftId;
}

/** The slice of a shift that spaced coloring reads. */
interface ColorableShift {
	id: string;
	jobIds: readonly string[];
	scheduledDate?: Date;
}

/**
 * shiftId → its color under the spaced scheme, for every shift given. A shift's place is the
 * boundary centers of the jobs attributed to it (see {@link shiftIdByJobId}); a job without a
 * boundary has no place, but still counts as a job. Shifts are placed soonest-scheduled first
 * (undated last, ties by id), so the result doesn't depend on the order they were fetched in.
 */
export function spacedShiftColors(shifts: readonly ColorableShift[], jobs: readonly Job[]): Map<string, string> {
	const shiftOfJob = shiftIdByJobId(shifts, jobs);
	const shiftsWithJobs = new Set(shiftOfJob.values());
	const pointsByShiftId = new Map<string, Position[]>();
	for (const job of jobs) {
		const shiftId = shiftOfJob.get(job.id);
		const geometry = shiftId ? getJobBoundaryGeometry(job) : null;
		if (!shiftId || !geometry) continue;
		const points = pointsByShiftId.get(shiftId) ?? [];
		points.push(polygonBboxCenter(geometry));
		pointsByShiftId.set(shiftId, points);
	}
	const when = (shift: ColorableShift) => shift.scheduledDate?.getTime() ?? Infinity;
	const ordered = shifts
		.map((shift) => ({
			id: shift.id,
			at: when(shift),
			points: pointsByShiftId.get(shift.id) ?? [],
			hasJobs: shift.jobIds.length > 0 || shiftsWithJobs.has(shift.id),
		}))
		.sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
	return assignSpacedShiftColors(ordered);
}
